import { useState } from 'react';

import Button from 'components/Button';
import IconText from 'components/IconText';
import Link from 'components/Link';
import Text from 'components/Text';
import { type FieldKey, useGlobalLinks } from 'hooks';
import type { Review as ReviewModel, ReviewImage } from 'models/api';
import type { ReviewScoreDistribution } from 'models/product';
import type { TemplateString } from 'types';
import { range } from 'utils/helpers';

import { useReviewContext } from '../ProductDetailsContext';

import LoadingReviewSkeleton from './LoadingReviewSkeleton';
import RatingSummary from './RatingSummary';
import Review from './Review';
import ReviewAiSummary from './ReviewAiSummary';
import ReviewImages from './ReviewImages';
import ReviewImagesDialog from './ReviewImagesDialog';
import ReviewsPopover from './ReviewsPopover';
import TrustvoiceLink from './TrustvoiceLink';

interface Props {
	className?: string;
	feedbackDoneText: string;
	filterSortingHeading: string;
	flagAltText: TemplateString | FieldKey;
	loadMoreButtonText: string;
	openReviewPopoverButtonText: string;
	ratingReviewCountText: TemplateString | FieldKey;
	readReviewButtonText: string;
	reportButtonText: string;
	reviewAiSummaryDescription: string;
	reviewAiSummaryHeading: string;
	reviewImageButtonLabel: string;
	reviewImagesDialogLabel: string;
	reviewOnlyWithImagesCheckboxLabel: string;
	reviewPolicyLinkText: string;
	reviewPolicyText: string;
	reviewProductNotRecommendedText: TemplateString | FieldKey;
	reviewProductRecommendedText: TemplateString | FieldKey;
	reviewScore: ReviewScoreDistribution | undefined;
	reviewScoreFilterDefaultOptionLabel: string;
	reviewScoreFilterLabel: string;
	reviewShowOriginalTextButtonText: string;
	reviewShowTranslationToLanguageButtonText: TemplateString | FieldKey;
	reviewSortingDateAscendingOptionLabel: string;
	reviewSortingDateDescendingOptionLabel: string;
	reviewSortingDefaultOptionLabel: string;
	reviewSortingLabel: string;
	reviewSortingRelevanceOptionLabel: string;
	reviewSortingScoreAscendingOptionLabel: string;
	reviewSortingScoreDescendingOptionLabel: string;
	reviewsPopoverTitle: string;
	reviewTranslatedFromLanguageText: TemplateString | FieldKey;
	reviewVerifiedBuyerLabel: string;
	singleReviewPopoverTitle: string;
	title: string;
	upvoteButtonText: string;
	viewReviewImagesButtonText: string;
}

export default function ReviewSummary({
	className,
	feedbackDoneText,
	filterSortingHeading,
	flagAltText,
	loadMoreButtonText,
	openReviewPopoverButtonText,
	ratingReviewCountText,
	readReviewButtonText,
	reportButtonText,
	reviewAiSummaryDescription,
	reviewAiSummaryHeading,
	reviewImageButtonLabel,
	reviewImagesDialogLabel,
	reviewOnlyWithImagesCheckboxLabel,
	reviewPolicyLinkText,
	reviewPolicyText,
	reviewProductNotRecommendedText,
	reviewProductRecommendedText,
	reviewScore,
	reviewScoreFilterDefaultOptionLabel,
	reviewScoreFilterLabel,
	reviewShowOriginalTextButtonText,
	reviewShowTranslationToLanguageButtonText,
	reviewSortingDateAscendingOptionLabel,
	reviewSortingDateDescendingOptionLabel,
	reviewSortingDefaultOptionLabel,
	reviewSortingLabel,
	reviewSortingRelevanceOptionLabel,
	reviewSortingScoreAscendingOptionLabel,
	reviewSortingScoreDescendingOptionLabel,
	reviewsPopoverTitle,
	reviewTranslatedFromLanguageText,
	reviewVerifiedBuyerLabel,
	singleReviewPopoverTitle,
	title,
	upvoteButtonText,
	viewReviewImagesButtonText,
}: Props) {
	const {
		closeReviewsPopover,
		fit3Summary,
		hasImagesFilter,
		isLoadingInitialReviews,
		isLoadingMoreReviews,
		isLoadingReviewImages,
		isLoadingReviews,
		isLoadingSelectedReviews,
		isReviewsPopoverOpen,
		loadMoreReviews,
		openReviewsPopover,
		reviewAiSummary,
		reviews,
		reviewsHasNextPage,
		selectedReviews,
		selectedReviewScore,
		selectedReviewsSortOption,
		setHasImagesFilter,
		setReviewScore,
		setReviewsSortOption,
	} = useReviewContext();
	const { reviewPolicy } = useGlobalLinks();

	const isLoadingSelectedSection =
		isLoadingSelectedReviews || isLoadingReviewImages;

	const [selectedReview, setSelectedReview] = useState<ReviewModel | undefined>(
		undefined,
	);
	const [selectedReviewImageIndex, setSelectedReviewImageIndex] = useState(0);
	const openReviewImageDialog = ({
		image,
		review,
	}: {
		image: ReviewImage;
		review: ReviewModel;
	}) => {
		setSelectedReview(review);
		const index = review.images?.findIndex((i) => i.large === image.large);
		// Just start from the first image if none is found for some reason.
		setSelectedReviewImageIndex(Math.min(index ?? 0, 0));
	};

	if (!reviewScore || reviewScore.score === 0) {
		return null;
	}

	return (
		<>
			<div className={className}>
				<Text as="h2" text={title} />

				<RatingSummary
					className="mt-r-lg"
					reviewScore={reviewScore.score}
					reviewCount={reviewScore.count}
					reviewCountText={ratingReviewCountText}
				/>

				<IconText
					className="mt-r-sm"
					iconColor="successActive"
					icon="circle-check-outlined"
					size="sm"
					text={
						<span>
							{reviewPolicyText}{' '}
							{reviewPolicy && (
								<Link href={reviewPolicy} underline>
									{reviewPolicyLinkText}
								</Link>
							)}
						</span>
					}
					as="p"
				/>

				<ReviewAiSummary
					className="mt-r-lg"
					description={reviewAiSummaryDescription}
					heading={reviewAiSummaryHeading}
					summary={reviewAiSummary}
				/>

				<ReviewImages
					className="mt-r-lg"
					dialogLabel={reviewImagesDialogLabel}
					feedbackDoneText={feedbackDoneText}
					flagAltText={flagAltText}
					readReviewButtonText={readReviewButtonText}
					reportButtonText={reportButtonText}
					reviewImageButtonLabel={reviewImageButtonLabel}
					singleReviewPopoverTitle={singleReviewPopoverTitle}
					reviewProductNotRecommendedText={reviewProductNotRecommendedText}
					reviewProductRecommendedText={reviewProductRecommendedText}
					reviewShowOriginalTextButtonText={reviewShowOriginalTextButtonText}
					reviewShowTranslationToLanguageButtonText={
						reviewShowTranslationToLanguageButtonText
					}
					reviewTranslatedFromLanguageText={reviewTranslatedFromLanguageText}
					reviewVerifiedBuyerLabel={reviewVerifiedBuyerLabel}
					upvoteButtonText={upvoteButtonText}
					viewReviewImagesButtonText={viewReviewImagesButtonText}
				/>

				<div className="mt-r-lg divide-y divide-strong border-b border-strong">
					{isLoadingSelectedSection &&
						range(3).map((index) => (
							<LoadingReviewSkeleton key={index} className="py-r-lg" />
						))}

					{!isLoadingSelectedSection &&
						selectedReviews?.map((review) => (
							<Review
								key={`${review.author}-${review.date}-${review.score}`}
								className="py-r-lg"
								feedbackDoneText={feedbackDoneText}
								flagAltText={flagAltText}
								onReviewImageClick={({ image }) => {
									openReviewImageDialog({ review, image });
								}}
								reportButtonText={reportButtonText}
								review={review}
								reviewImageButtonLabel={reviewImageButtonLabel}
								upvoteButtonText={upvoteButtonText}
								verifiedBuyerLabel={reviewVerifiedBuyerLabel}
								showOriginalTextButtonText={reviewShowOriginalTextButtonText}
								showTranslationToLanguageButtonText={
									reviewShowTranslationToLanguageButtonText
								}
								translatedFromLanguageText={reviewTranslatedFromLanguageText}
								productRecommendedText={reviewProductRecommendedText}
								productNotRecommendedText={reviewProductNotRecommendedText}
							/>
						))}
				</div>

				<div className="mt-6 flex items-center justify-between gap-y-6 max-md:flex-col">
					<Button variant="branded" onClick={openReviewsPopover}>
						{`${openReviewPopoverButtonText} (${reviewScore?.count})`}
					</Button>
					<TrustvoiceLink />
				</div>
			</div>

			<ReviewsPopover
				feedbackDoneText={feedbackDoneText}
				flagAltText={flagAltText}
				reportButtonText={reportButtonText}
				reviewAiSummary={reviewAiSummary}
				reviewAiSummaryDescription={reviewAiSummaryDescription}
				reviewAiSummaryHeading={reviewAiSummaryHeading}
				reviewImageButtonLabel={reviewImageButtonLabel}
				upvoteButtonText={upvoteButtonText}
				title={reviewsPopoverTitle}
				isOpen={isReviewsPopoverOpen}
				onClose={closeReviewsPopover}
				filterSortingHeading={filterSortingHeading}
				isLoadingMoreReviews={isLoadingMoreReviews}
				hasImagesFilter={hasImagesFilter}
				onHasImagesFilterChange={(hasImages) => setHasImagesFilter(hasImages)}
				hasMoreReviews={reviewsHasNextPage}
				loadMoreButtonText={loadMoreButtonText}
				onLoadMoreClick={loadMoreReviews}
				reviewCountPerGrade={reviewScore.countPerGrade}
				reviews={reviews}
				reviewPolicyLinkText={reviewPolicyLinkText}
				reviewPolicyText={reviewPolicyText}
				onReviewImageClick={({ image, review }) => {
					openReviewImageDialog({ review, image });
				}}
				reviewPolicyUrl={reviewPolicy}
				score={reviewScore.score}
				totalReviews={reviewScore.count || 0}
				fit3Summary={fit3Summary}
				onScoreChange={(score) => setReviewScore(score)}
				selectedReviewScore={selectedReviewScore}
				sortOption={selectedReviewsSortOption}
				onSortOptionChange={(option) => setReviewsSortOption(option)}
				isLoading={isLoadingReviews}
				isLoadingInitial={isLoadingInitialReviews}
				reviewVerifiedBuyerLabel={reviewVerifiedBuyerLabel}
				ratingReviewCountText={ratingReviewCountText}
				reviewOnlyWithImagesCheckboxLabel={reviewOnlyWithImagesCheckboxLabel}
				reviewShowOriginalTextButtonText={reviewShowOriginalTextButtonText}
				reviewShowTranslationToLanguageButtonText={
					reviewShowTranslationToLanguageButtonText
				}
				reviewTranslatedFromLanguageText={reviewTranslatedFromLanguageText}
				reviewProductRecommendedText={reviewProductRecommendedText}
				reviewProductNotRecommendedText={reviewProductNotRecommendedText}
				reviewScoreFilterDefaultOptionLabel={
					reviewScoreFilterDefaultOptionLabel
				}
				reviewScoreFilterLabel={reviewScoreFilterLabel}
				reviewSortingDateAscendingOptionLabel={
					reviewSortingDateAscendingOptionLabel
				}
				reviewSortingDateDescendingOptionLabel={
					reviewSortingDateDescendingOptionLabel
				}
				reviewSortingDefaultOptionLabel={reviewSortingDefaultOptionLabel}
				reviewSortingLabel={reviewSortingLabel}
				reviewSortingRelevanceOptionLabel={reviewSortingRelevanceOptionLabel}
				reviewSortingScoreAscendingOptionLabel={
					reviewSortingScoreAscendingOptionLabel
				}
				reviewSortingScoreDescendingOptionLabel={
					reviewSortingScoreDescendingOptionLabel
				}
			/>

			<ReviewImagesDialog
				id="single-review-images-dialog"
				showThumbnails={false}
				isOpen={Boolean(selectedReview)}
				label={
					selectedReview?.author
						? `${reviewImagesDialogLabel}, ${selectedReview.author}`
						: reviewImagesDialogLabel
				}
				onClose={() => setSelectedReview(undefined)}
				images={selectedReview?.images}
				selectedImageIndex={selectedReviewImageIndex}
				setSelectedImageIndex={setSelectedReviewImageIndex}
			/>
		</>
	);
}
ReviewSummary.displayName = 'ReviewSummary';
